import React, { useState } from 'react';
import { Users, BarChart3, Settings, Shield, Menu, X, ChevronLeft, Search, Archive, FolderOpen, MapPin, Home, Calendar, Plus, Bot } from 'lucide-react';
import VaultBrand from './VaultBrand';
import { useGroupContext } from '../lib/groups/context';
// GroupSelector and group context removed for single-vault model

interface SidebarProps {
  currentView: string;
  onViewChange: (view: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  isCollapsed?: boolean;
  onCollapse?: () => void;
  onSearchContacts: () => void;
}

const navigation = [
  { name: 'Assistant', id: 'assistant', icon: Bot },
  { name: 'Contacts', id: 'contacts', icon: Users },
  { name: 'Collections', id: 'collections', icon: FolderOpen },
  { name: 'Map', id: 'map', icon: MapPin },
  { name: 'Calendar', id: 'calendar', icon: Calendar },
];

export default function Sidebar({
  currentView,
  onViewChange,
  isOpen,
  onToggle,
  isCollapsed = false,
  onCollapse,
  onSearchContacts
}: SidebarProps) {
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const { reload, activeGroupId, groups } = useGroupContext();
  const defaultLogo = "https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp";
  const activeGroup = groups.find(g => g.group.id === activeGroupId);
  const baseLogoUrl = (activeGroup?.group as any)?.image_url || defaultLogo;
  // Cache-bust the logo when Settings updates it by using a localStorage timestamp
  let logoUrl = baseLogoUrl;
  try {
    const b = activeGroupId ? localStorage.getItem(`gv:logoBust:${activeGroupId}`) : null;
    if (b) {
      logoUrl = `${baseLogoUrl}${baseLogoUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(b)}`;
    }
  } catch { /* ignore */ }

  const handleChangeAccount = () => {
    // Navigate to home page to select/access different vaults without full reload
    try {
      window.history.pushState({}, '', '/');
      reload();
    } catch {
      window.location.href = '/';
    }
  };
  // Treat collapse only as a large-screen concept; mobile should always appear expanded visually.
  const collapsedDesktop = isCollapsed; // semantic name retained; styling below is guarded with lg: utilities
  return (
    <div className={`
      w-64 ${collapsedDesktop ? 'lg:w-16' : 'lg:w-64'} bg-black border-r border-gray-800 text-white flex flex-col h-screen
      fixed z-[9999] transition-transform duration-300 ease-in-out
      ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>

      {/* Header Section */}
      <div className="flex-shrink-0 p-6">
        {/* Mobile header (always) */}
        <div className="flex items-center justify-between lg:hidden">
          <button
            onClick={() => {
              onViewChange('dashboard');
              if (window.innerWidth < 1024) onToggle();
            }}
            title="Home"
            className="hover:opacity-80 transition-opacity flex items-center gap-3"
          >
            <img src={logoUrl} alt="Vault Logo" className="h-8 w-8 object-contain" />
            <span className="font-semibold tracking-wide text-white text-sm">Griswoldvault</span>
          </button>
          <button
            onClick={onToggle}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Desktop expanded header */}
        {!collapsedDesktop && (
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => onViewChange('dashboard')}
                title="Home"
                className="hover:opacity-80 transition-opacity"
              >
                <img src={logoUrl} alt="Vault Logo" className="h-8 w-8 object-contain" />
              </button>
              <div className="flex items-center gap-2">
                <VaultBrand onNavigate={() => onViewChange('dashboard')} />
              </div>
            </div>
            {onCollapse && (
              <button
                onClick={onCollapse}
                className="w-6 h-6 items-center justify-center text-gray-400 hover:text-white transition-colors hidden lg:flex"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Desktop collapsed header */}
        {collapsedDesktop && (
          <div className="hidden lg:flex items-center justify-center">
            <button
              onClick={() => onViewChange('dashboard')}
              title="Dashboard"
              className="flex justify-center hover:opacity-80 transition-opacity"
            >
              <img src={logoUrl} alt="Vault Logo" className="h-8 w-8 object-contain" />
            </button>
          </div>
        )}

        {/* Expand button when collapsed (desktop) */}
        {collapsedDesktop && onCollapse && (
          <div className="mt-4 hidden lg:flex justify-center">
            <button
              onClick={onCollapse}
              className="w-8 h-8 items-center justify-center text-gray-400 hover:text-white hover:bg-gray-900 rounded-lg transition-colors flex"
              title="Expand Sidebar"
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Group selector removed for single-vault model */}

      {/* Navigation Section */}
      <nav className="flex-1 px-4 pb-4 min-h-0">
        <div className="space-y-2">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onViewChange(item.id);
                  // Close sidebar on mobile after selection
                  if (window.innerWidth < 1024) {
                    onToggle();
                  }
                }}
                title={collapsedDesktop ? item.name : undefined}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${currentView === item.id ? 'bg-gray-800 text-white border border-gray-700' : 'text-gray-400 hover:bg-gray-900 hover:text-white'
                  } ${collapsedDesktop ? 'lg:justify-center' : ''}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {/* Show labels everywhere; hide only on large screens when collapsed */}
                <span className={`font-medium ${collapsedDesktop ? 'lg:hidden' : ''}`}>{item.name}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Footer Section (desktop expanded) - Move ADD CONTACT above navigation (now a floating primary button area) */}
      {!collapsedDesktop && (
        <div className="flex-shrink-0 p-4 border-t border-gray-800 space-y-4 hidden lg:block">
          {/* Primary Add Contact button elevated above other utilities */}
          <div>
            <button
              onClick={() => {
                onViewChange('quick-add-contact');
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left bg-green-600 text-white hover:bg-green-700 transition-colors shadow-lg shadow-green-900/30"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium tracking-wide">ADD CONTACT</span>
            </button>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => {
                onSearchContacts();
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left bg-gray-900 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors border border-gray-700"
            >
              <Search className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium tracking-wide">SEARCH</span>
            </button>
            <button
              onClick={() => {
                onViewChange('settings');
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
              className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left text-gray-400 hover:bg-gray-900 hover:text-white transition-colors"
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium tracking-wide">SETTINGS</span>
            </button>
            <button
              onClick={handleChangeAccount}
              className="w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-left text-blue-400 hover:bg-blue-900/20 hover:text-blue-300 transition-colors"
            >
              <Home className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium tracking-wide">VAULT</span>
            </button>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <div className="w-2 h-2 bg-green-400 animate-pulse flex-shrink-0" style={{ borderRadius: '9999px' }}></div>
              <span className="font-medium tracking-wide">SECURE SESSION</span>
            </div>
          </div>
        </div>
      )}

      {/* Collapsed footer (desktop only) */}
      {collapsedDesktop && (
        <div className="flex-shrink-0 p-4 border-t border-gray-800 space-y-4 hidden lg:block">
          <div className="flex flex-col space-y-2">
            <button
              onClick={() => {
                onSearchContacts();
                // Close sidebar on mobile after selection
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
              className="w-full flex justify-center p-3 rounded-lg bg-gray-900 text-gray-300 hover:bg-gray-800 hover:text-white transition-colors border border-gray-700"
              title="Search Contacts"
            >
              <Search className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                onViewChange('settings');
                // Close sidebar on mobile after selection
                if (window.innerWidth < 1024) {
                  onToggle();
                }
              }}
              className="w-full flex justify-center p-2 rounded-lg text-gray-400 hover:bg-gray-900 hover:text-white transition-colors"
              title="Settings"
            >
              <Settings className="w-4 h-4" />
            </button>
            <button
              onClick={handleChangeAccount}
              className="w-full flex justify-center p-2 rounded-lg text-blue-400 hover:bg-blue-900/20 hover:text-blue-300 transition-colors"
              title="Vault"
            >
              <Home className="w-4 h-4" />
            </button>
          </div>
          <div className="flex justify-center">
            <div className="w-2 h-2 bg-green-400 animate-pulse" style={{ borderRadius: '9999px' }}></div>
          </div>
        </div>
      )}

      {/* Mobile footer: Promote ADD CONTACT as a prominent bar above utilities */}
      <div className="flex-shrink-0 border-t border-gray-800 lg:hidden">
        <button
          onClick={() => {
            onViewChange('quick-add-contact');
            if (window.innerWidth < 1024) {
              onToggle();
            }
          }}
          className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-medium tracking-wide"
          title="Add Contact"
        >
          <Plus className="w-4 h-4" />
          ADD CONTACT
        </button>
        <div className="grid grid-cols-3 gap-px bg-gray-800">
          <button
            onClick={() => {
              onSearchContacts();
              if (window.innerWidth < 1024) {
                onToggle();
              }
            }}
            className="flex flex-col items-center justify-center py-3 bg-black text-gray-200 hover:bg-gray-900 transition-colors"
            title="Search"
          >
            <Search className="w-4 h-4 mb-1" />
            <span className="text-[10px] font-medium tracking-wide">SEARCH</span>
          </button>
          <button
            onClick={() => {
              onViewChange('settings');
              if (window.innerWidth < 1024) {
                onToggle();
              }
            }}
            className="flex flex-col items-center justify-center py-3 bg-black text-gray-400 hover:text-white hover:bg-gray-900 transition-colors"
            title="Settings"
          >
            <Settings className="w-4 h-4 mb-1" />
            <span className="text-[10px] font-medium tracking-wide">SETTINGS</span>
          </button>
          <button
            onClick={handleChangeAccount}
            className="flex flex-col items-center justify-center py-3 bg-black text-blue-400 hover:bg-blue-900/20 hover:text-blue-300 transition-colors"
            title="Vault"
          >
            <Home className="w-4 h-4 mb-1" />
            <span className="text-[10px] font-medium tracking-wide">VAULT</span>
          </button>
        </div>
        <div className="flex items-center justify-center py-1 text-[10px] text-gray-500 bg-black border-t border-gray-800">
          <div className="w-2 h-2 bg-green-400 animate-pulse mr-2" style={{ borderRadius: '9999px' }}></div>
          <span className="font-medium tracking-wide">SECURE SESSION</span>
        </div>
      </div>
    </div>
  );
}